import './StatisticsView.scss'
import { useTranslation } from 'react-i18next'
import { Statistics } from '../../model/domain/statistics'
import {
    Alert,
    AlertType,
    Button,
    DateField,
    LoadingView,
    SelectorField,
} from '@cuatroochenta/co-admin-react-library'
import { translate } from '../../../I18n/i18n'
import { Col, OverlayTrigger, Row, Table, Tooltip } from 'react-bootstrap'
import { Center } from '../../model/domain/center'
import { useMemo } from 'react'

interface StatisticsViewProps {
    centerId: number | null
    dateFrom: Date
    dateTo: Date
    centers?: Center[]
    statistics?: Statistics[]
    initialLoading: boolean
    loadingResults: boolean
    error: string | null
    onChangeCenterId: (centerId: number | null) => void
    onChangeDateFrom: (dateFrom: string) => void
    onChangeDateTo: (dateTo: string) => void
    onFilter: () => void
}

export default function StatisticsView({
    centerId,
    dateFrom,
    dateTo,
    centers,
    statistics,
    initialLoading,
    loadingResults,
    error,
    onChangeCenterId,
    onChangeDateFrom,
    onChangeDateTo,
    onFilter,
}: StatisticsViewProps) {
    const { t } = useTranslation()

    const centerSelectorOptions = useMemo(() => {
        return (
            centers?.map((center) => ({
                id: center.id,
                description: center.name,
            })) ?? []
        )
    }, [centers])

    if (initialLoading) {
        return <LoadingView loading />
    }

    return (
        <>
            <Alert
                show={!!error}
                type={AlertType.DANGER}
                message={translate(t, 'common.TR_ERROR_GENERIC')}
            />
            <Row className="statistics-filters-row">
                <Col>
                    <label htmlFor="centerId" className="mb-1">
                        {translate(t, 'statistics.TR_FILTER_CENTER')}
                    </label>
                    <SelectorField
                        name="centerId"
                        value={centerId?.toString() ?? ''}
                        includeAllOption
                        values={centerSelectorOptions}
                        onChange={(value) =>
                            onChangeCenterId(value ? +value : null)
                        }
                    />
                </Col>

                <Col className="d-flex flex-column">
                    <label htmlFor="dateFrom" className="mb-1">
                        {translate(t, 'statistics.TR_FILTER_DATE_FROM')}
                    </label>
                    <DateField
                        name="dateFrom"
                        type="DATE"
                        value={dateFrom.toISOString()}
                        onChange={onChangeDateFrom}
                        maxDate={dateTo}
                        readOnly
                    />
                </Col>

                <Col className="d-flex flex-column">
                    <label htmlFor="dateTo" className="mb-1">
                        {translate(t, 'statistics.TR_FILTER_DATE_TO')}
                    </label>
                    <DateField
                        name="dateTo"
                        type="DATE"
                        value={dateTo.toISOString()}
                        onChange={onChangeDateTo}
                        minDate={dateFrom}
                        readOnly
                    />
                </Col>

                <Col>
                    <Button
                        onClick={onFilter}
                        content={translate(t, 'statistics.TR_APPLY')}
                    />
                </Col>
            </Row>

            {loadingResults || statistics ? (
                <StatisticsResultsView
                    statistics={statistics}
                    loadingResults={loadingResults}
                />
            ) : null}
        </>
    )
}

interface StatisticsResultsProps {
    statistics: StatisticsViewProps['statistics']
    loadingResults: StatisticsViewProps['loadingResults']
}

function StatisticsResultsView({
    statistics,
    loadingResults,
}: StatisticsResultsProps) {
    const { t } = useTranslation()

    const columnsConfig = useMemo(
        () => [
            {
                key: 'centerName',
                headerLabelKey: 'common.TR_CENTER_LABEL',
                tooltipTextKey: null,
            },
            {
                key: 'totalResidents',
                headerLabelKey: 'statistics.TR_TOTAL_RESIDENTS_LABEL',
                tooltipTextKey: 'statistics.TR_TOTAL_RESIDENTS_TOOLTIP',
            },
            {
                key: 'totalUsers',
                headerLabelKey: 'statistics.TR_TOTAL_USERS_LABEL',
                tooltipTextKey: 'statistics.TR_TOTAL_USERS_TOOLTIP',
            },
            {
                key: 'registeredResidents',
                headerLabelKey: 'statistics.TR_REGISTERED_RESIDENTS_LABEL',
                tooltipTextKey: 'statistics.TR_REGISTERED_RESIDENTS_TOOLTIP',
            },
            {
                key: 'registeredUsers',
                headerLabelKey: 'statistics.TR_REGISTERED_USERS_LABEL',
                tooltipTextKey: 'statistics.TR_REGISTERED_USERS_TOOLTIP',
            },
            {
                key: 'totalNews',
                headerLabelKey: 'statistics.TR_TOTAL_NEWS_LABEL',
                tooltipTextKey: 'statistics.TR_TOTAL_NEWS_TOOLTIP',
            },
        ],
        []
    )

    if (loadingResults) {
        return <LoadingView loading />
    }

    if (statistics!.length === 0) {
        return (
            <p className="mt-3">{translate(t, 'common.TR_NO_RESULTS_LABEL')}</p>
        )
    }

    return (
        <>
            <Table className="mt-3">
                <thead>
                    <tr>
                        {columnsConfig.map((column, index) => (
                            <th key={index}>
                                <span className="mr-2">
                                    {translate(t, column.headerLabelKey)}
                                </span>
                                {column.tooltipTextKey ? (
                                    <OverlayTrigger
                                        overlay={
                                            <Tooltip id={`tooltip-${index}`}>
                                                {translate(
                                                    t,
                                                    column.tooltipTextKey
                                                )}
                                            </Tooltip>
                                        }
                                    >
                                        <i className="fa fa-circle-info" />
                                    </OverlayTrigger>
                                ) : null}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {statistics?.map((stat, index) => (
                        <tr
                            key={stat.centerId}
                            className={index % 2 !== 0 ? 'odd' : ''}
                        >
                            <td>{stat.centerName}</td>
                            <td>{stat.totalResidents}</td>
                            <td>{stat.totalUsers}</td>
                            <td>{stat.registeredResidents}</td>
                            <td>{stat.registeredUsers}</td>
                            <td>{stat.totalNews}</td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        </>
    )
}
